import { Extension } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    lineHeight: {
      setLineHeight: (lineHeight: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }

  interface Storage {
    lineHeight: LineHeightStorage;
  }
}

type LineHeightOptions = {
  defaultValue: string;
  types: string[];
};

type LineHeightStorage = {
  currentLineHeight: string | null;
};

const DEFAULT = 'default';

export const LineHeight = Extension.create<
  LineHeightOptions,
  LineHeightStorage
>({
  name: 'lineHeight',

  addOptions() {
    return {
      // The configurable `defaultValue` option is what will be used when the lineHeight
      // setting is set to the string value 'default' rather than a number.
      defaultValue: '1.25',
      types: ['paragraph'],
    };
  },

  addStorage() {
    return {
      currentLineHeight: null,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            parseHTML: (element) => element.getAttribute('data-line-height'),
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) {
                if (this.storage.currentLineHeight) {
                  const value =
                    this.storage.currentLineHeight === DEFAULT
                      ? this.options.defaultValue
                      : this.storage.currentLineHeight;

                  return {
                    style: `line-height: ${value}`,
                    'data-line-height': this.storage.currentLineHeight,
                  };
                }

                return {
                  style: `line-height: ${this.options.defaultValue}`,
                  'data-line-height': DEFAULT,
                };
              }

              const value =
                attributes.lineHeight === DEFAULT
                  ? this.options.defaultValue
                  : attributes.lineHeight;

              return {
                style: `line-height: ${value}`,
                'data-line-height': attributes.lineHeight,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ commands }) => {
          return this.options.types.every((type) =>
            commands.updateAttributes(type, {
              lineHeight,
              'data-line-height': lineHeight,
            })
          );
        },
      unsetLineHeight:
        () =>
        ({ commands }) => {
          return this.options.types.every((type) =>
            commands.updateAttributes(type, {
              lineHeight: DEFAULT,
              'data-line-height': DEFAULT,
            })
          );
        },
    };
  },
});
